import { Card, CardContent, IconButton, styled } from '@mui/material'
import Icon from '@mui/material/Icon'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import React from 'react'

import { SchemaOrgUser } from '@/__generated/es-api'
import CreateButton from '@/app/component/atom/create-button'
import Loader from '@/app/component/atom/loader'
import SupportedIcon from '@/app/component/atom/supported-icon'
import TabHeader from '@/app/component/atom/tab-header'
import CustomTooltip from '@/app/component/atom/tooltip'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { useGetPlan } from '@/app/module/plans/hooks/use-get-plan'
import { useDeleteInvitation } from '@/app/module/user/hooks/use-delete-invitation'
import { useDeleteUser } from '@/app/module/user/hooks/use-delete-user'
import { useGetInvitations } from '@/app/module/user/hooks/use-get-invitations'
import { useGetUsers } from '@/app/module/user/hooks/use-get-users'
import { CommonUserType } from '@/app/module/user/types'
import { formatDate } from '@/app/service/util'
import { formatBooleanVal, formatName, formatRole } from '@/app/service/util/format'
import { Permissions, UserRole } from '@/app/types'

import UserAccessContainer from './user-access.container'
import UserAccountsUsage from './user-accounts-usage'
import UserActionMenu from './user-action-menu'
import UserInviteDialog from './user-invite-dialog'

type Props = {
  permissions: Permissions
  role: UserRole
  userId: number
}

const Users: React.FC<Props> = ({ permissions, userId }) => {
  const { data: orgPlan, refetch: refetchOrgPlan } = useGetPlan()
  const [isInviteDialogOpen, setInviteDialogOpen] = React.useState(false)
  const [currentUser, setCurrentUser] = React.useState<SchemaOrgUser | undefined>(undefined)
  const createConfirmation = useConfirmation()

  const { data: invitationsData, isFetching: loadingInvitationsData, refetch: refetchInvitations } = useGetInvitations()
  const [deleteInvitation, { isLoading: deletingInvitation }] = useDeleteInvitation()
  const { data: usersData, isFetching: loadingUsersData, refetch: refetchUsers } = useGetUsers()
  const [deleteUser, { isLoading: deletingUser }] = useDeleteUser()

  const invitationCount = invitationsData?.invites?.length || 0

  const canInviteMoreUsers = React.useMemo(
    () => orgPlan && orgPlan.usersNow + invitationCount < orgPlan.usersIncluded,
    [invitationCount, orgPlan],
  )

  const refetch = React.useCallback(() => {
    refetchInvitations()
    refetchOrgPlan()
    refetchUsers()
  }, [refetchInvitations, refetchOrgPlan, refetchUsers])

  const handleDeleteInvitation = React.useCallback(
    async (inviteId: number) => {
      const confirmed = await createConfirmation({
        title: 'Delete open invitation',
        content: 'Are you sure you want to delete the open invitation?',
        confirmButtonLabel: 'Delete',
        cancelButtonLabel: 'Cancel',
      })
      if (confirmed) {
        await deleteInvitation(inviteId)
      }
    },
    [createConfirmation, deleteInvitation],
  )
  const handleDeleteUser = React.useCallback(
    async (user: CommonUserType) => {
      const confirmed = await createConfirmation({
        title: 'Remove user from organization',
        content: `Please confirm you want to remove user ${user.name} (${user.email}) from the organization.`,
        confirmButtonLabel: 'Remove',
        cancelButtonLabel: 'Cancel',
      })
      if (confirmed) {
        await deleteUser(user.id)
      }
    },
    [createConfirmation, deleteUser],
  )

  const formattedInvitations = React.useMemo<CommonUserType[]>(
    () =>
      (invitationsData?.invites || []).map((invite) => ({
        id: invite.inviteId,
        created: invite.created,
        email: invite.email,
        fullAccess: '',
        inviteId: invite.inviteId,
        invitationSentDate: formatDate(invite.created),
        name: formatName(invite.firstName, invite.lastName),
        notify: '',
        role: formatRole(invite.role),
        twoFactorAuthenticated: '',
      })),
    [invitationsData],
  )
  const formattedUsers = React.useMemo<CommonUserType[]>(
    () =>
      (usersData?.users || []).map((user) => {
        if (user.role === 'customer_service') {
          return {
            id: Date.now(),
            email: '',
            invitationSentDate: '',
            name: '',
            role: formatRole(user.role),
            twoFactorAuthenticated: '',
            fullAccess: '',
            notify: '',
          }
        }
        const formattedUserData = {
          ...user,
          role: formatRole(user.role),
          email: user.username,
          twoFactorAuthenticated: formatBooleanVal(user.totp),
          fullAccess: formatBooleanVal(user.fullAccess),
          invitationSentDate: '',
          notify: formatBooleanVal(user.notify),
        }
        return formattedUserData
      }),
    [usersData],
  )

  const data: CommonUserType[] = React.useMemo(() => {
    const allUsers = formattedInvitations.concat(formattedUsers)
    return [...allUsers].sort((a, b) => {
      if (a.inviteId) {
        return 1
      }
      if (b.inviteId) {
        return -1
      }
      if (!a.role) {
        return 1
      }
      if (!b.role) {
        return -1
      }
      if (a.role === 'Owner' || b.role === 'Customer Support') {
        return -1
      }
      if (b.role === 'Owner' || a.role === 'Customer Support') {
        return 1
      }
      if (a.role === 'Manager') {
        return -1
      }
      return 1
    })
  }, [formattedInvitations, formattedUsers])

  const loading = loadingInvitationsData || loadingUsersData || deletingInvitation || deletingUser

  return (
    <>
      {!!currentUser && <UserAccessContainer data={currentUser} onClose={() => setCurrentUser(undefined)} />}

      <Container>
        <TabHeader
          title="All Users"
          action={
            <TableActionsContainer>
              {permissions.invites && (
                <CustomTooltip
                  title={
                    canInviteMoreUsers
                      ? ''
                      : "You've reached the user limit of your plan. To add more, please upgrade your plan."
                  }
                >
                  <InviteContainer>
                    <UserAccountsUsage />
                    <CreateButton
                      disabled={!canInviteMoreUsers}
                      startIcon={<Icon>person_add</Icon>}
                      onClick={() => setInviteDialogOpen(true)}
                    >
                      Invite a user
                    </CreateButton>
                  </InviteContainer>
                </CustomTooltip>
              )}
              <CustomTooltip title="Reload users and invitations">
                <IconButton disabled={loading} onClick={refetch}>
                  <Icon>refresh</Icon>
                </IconButton>
              </CustomTooltip>
            </TableActionsContainer>
          }
        />
        <ContentContainer>
          <TableContainer>
            <Table size="small" className="invitations-table">
              <TableHead>
                <TableRow>
                  <TableHeadCell>Invite Date</TableHeadCell>
                  <TableHeadCell>Email</TableHeadCell>
                  <TableHeadCell>Name</TableHeadCell>
                  <TableHeadCell>Status</TableHeadCell>
                  <TableHeadCell align="center">2FA</TableHeadCell>
                  <TableHeadCell>Role</TableHeadCell>
                  <TableHeadCell align="center">Full access</TableHeadCell>
                  <TableHeadCell align="center">Email notifications</TableHeadCell>
                  <TableHeadCell align="center" width="90px">
                    Actions
                  </TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => {
                  const isInvitation = !!row.inviteId
                  const isDeleteVisible =
                    permissions.manageUsers &&
                    !isInvitation &&
                    row.role !== 'Owner' &&
                    row.role !== 'Customer Support' &&
                    row.id !== userId
                  const isRoleEditable =
                    permissions.manageUsers && !isInvitation && row.id !== userId && row.role !== 'Owner'

                  return (
                    <TableRow key={row.id}>
                      <TableCell>{formatDate(row.created)}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <StatusCell isPending={!!row.inviteId}>{row.inviteId ? 'INVITE SENT' : 'ACTIVE'}</StatusCell>
                      <TableCell align="center">
                        <SupportedIconContainer>
                          <SupportedIcon supported={row.twoFactorAuthenticated === 'YES'} />
                        </SupportedIconContainer>
                      </TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell align="center">
                        <SupportedIconContainer>
                          <SupportedIcon hideNotSupported={true} supported={row.fullAccess === 'YES'} />
                        </SupportedIconContainer>
                      </TableCell>
                      <TableCell align="center">
                        <SupportedIconContainer>
                          <SupportedIcon hideNotSupported={true} supported={row.notify === 'YES'} />
                        </SupportedIconContainer>
                      </TableCell>
                      <TableCell>
                        <ActionsContainer>
                          <UserActionMenu
                            user={row}
                            isRoleEditable={isRoleEditable}
                            isInvitation={isInvitation}
                            isDeleteVisible={isDeleteVisible}
                            onDeleteInvitation={handleDeleteInvitation}
                            onManageRole={(user) =>
                              setCurrentUser(usersData?.users?.find((u) => u.id === user.id) || undefined)
                            }
                            onRemoveUser={handleDeleteUser}
                          />
                        </ActionsContainer>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {!data.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="h6" color="textSecondary" align="center" sx={{ padding: 2 }}>
                        No users or invitations were found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ContentContainer>
        {loading && <Loader />}
      </Container>
      <UserInviteDialog open={isInviteDialogOpen} onClose={() => setInviteDialogOpen(false)} />
    </>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  maxHeight: '100%',
})

const ContentContainer = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100% - 64px)', // Adjust height to account for header
})

const TableActionsContainer = styled('div')({
  display: 'flex',
  gap: 8,
})

const TableContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  overflow: 'auto',

  '& table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderTop: 'none',
  },
})

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const StatusCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'isPending',
})<TableCellProps & { isPending: boolean }>(({ isPending, theme }) => ({
  color: isPending ? theme.palette.warning.main : theme.palette.success.dark,
  fontWeight: 'bold',
}))

const ActionsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  '& > div': {
    marginLeft: 0,
    marginRight: 0,
  },
})

const InviteContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
})

const SupportedIconContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

export default Users
